/**
 * How the Places list is ordered and read.
 *
 * The page's one question is **what is waiting for me here** — a place exists
 * so that walking in fires a reminder, and until 2026-09-08 the list could not
 * answer it at all: a name, coordinates, a radius and a map say nothing about
 * whether anything will happen when you arrive.
 *
 * So the sections are that question, not the alphabet: places with something
 * waiting come first, and everything else is a quiet list underneath. Pure and
 * import-free so a node probe can check it (`npm run test:places`).
 */

/**
 * The minimum a place must have for these rules to work. Kept structural
 * rather than a copy of the entity so the page's real `Place` flows straight
 * through — a second definition of a place is a second thing to keep in step.
 */
export interface PlaceRow {
  id: number
  title: string
  address?: string | null
  tags?: string[]
  waiting_count?: number
  waiting?: { id: number | string; title: string }[]
}

export type PlaceSectionId = 'waiting' | 'quiet'

export interface PlaceSection<T extends PlaceRow = PlaceRow> {
  id: PlaceSectionId
  title: string
  /** One line under the heading. Said once, not repeated on every row. */
  blurb: string
  places: T[]
}

const count = (p: PlaceRow) => Math.max(0, p.waiting_count ?? 0)

/** Most waiting first, then alphabetical — a stable order on every render. */
function byUrgencyThenName(a: PlaceRow, b: PlaceRow): number {
  const d = count(b) - count(a)
  if (d !== 0) return d
  return (a.title || '').localeCompare(b.title || '')
}

export function sectionPlaces<T extends PlaceRow>(places: T[]): PlaceSection<T>[] {
  const waiting = places.filter((p) => count(p) > 0).sort(byUrgencyThenName)
  const quiet = places.filter((p) => count(p) === 0).sort(byUrgencyThenName)
  const out: PlaceSection<T>[] = []
  if (waiting.length) {
    out.push({
      id: 'waiting',
      title: 'Something waiting here',
      blurb: 'You will be reminded as you walk in.',
      places: waiting,
    })
  }
  if (quiet.length) {
    out.push({
      id: 'quiet',
      title: waiting.length ? 'Nothing waiting yet' : 'Your places',
      blurb: 'Tie a task to one of these and it will find you when you arrive.',
      places: quiet,
    })
  }
  return out
}

/** "2 things waiting" — the one quiet line on a row. */
export function waitingLine(p: PlaceRow): string {
  const n = count(p)
  if (n === 0) return 'Nothing waiting'
  const names = (p.waiting || []).map((w) => w.title).filter(Boolean)
  if (names.length === 0) return n === 1 ? '1 thing waiting' : `${n} things waiting`
  if (n <= names.length) return names.join(' · ')
  return `${names.join(' · ')} +${n - names.length} more`
}

/** The summary line under the page title. Words, not counts alone. */
export function placesSummary(places: PlaceRow[]): string {
  if (places.length === 0) return 'Nowhere saved yet.'
  const withWork = places.filter((p) => count(p) > 0)
  const total = withWork.reduce((s, p) => s + count(p), 0)
  const placeWord = places.length === 1 ? '1 place' : `${places.length} places`
  if (total === 0) return `${placeWord}, nothing waiting at any of them.`
  const thing = total === 1 ? '1 thing' : `${total} things`
  const where = withWork.length === 1 ? 'one of them' : `${withWork.length} of them`
  return `${placeWord} · ${thing} waiting at ${where}.`
}

/** Free-text match over the things a person would actually type. */
export function matchesQuery(p: PlaceRow, q: string): boolean {
  const needle = q.trim().toLowerCase()
  if (!needle) return true
  const hay = [
    p.title || '',
    p.address || '',
    ...(p.tags || []),
    ...((p.waiting || []).map((w) => w.title)),
  ].join(' ').toLowerCase()
  return hay.includes(needle)
}
